/**
 * Shapes returned by the JSON API of the portal.
 */

export interface PostResponse {
    readonly id: number;
    readonly slug: string;
    readonly title: string;
    readonly excerpt: string;
    readonly author: string;
    readonly category: string | null;
    readonly reading_minutes: number;
    readonly image_url: string | null;
    readonly is_published: boolean;
    readonly is_pinned: boolean;
    readonly view_count: number;
    readonly published_at: string | null;
    readonly created_at: string;
}

export interface PostListResponse {
    readonly count: number;
    readonly posts: readonly PostResponse[];
}

export interface FeedEntryResponse {
    readonly kind: string;
    readonly id: number;
    readonly url: string;
    readonly title: string;
    readonly summary: string;
    readonly category: string;
    readonly author: string;
    readonly published_at: string;
    readonly image_url: string | null;
    readonly reading_minutes: number;
    readonly view_count: number;
    readonly version: string | null;
}

export interface FeedResponse {
    readonly total: number;
    readonly offset: number;
    readonly has_more: boolean;
    readonly items: readonly FeedEntryResponse[];
}

export interface NotificationResponse {
    readonly id: number;
    readonly kind: string;
    readonly kind_label: string;
    readonly icon: string;
    readonly title: string;
    readonly body: string;
    readonly url: string | null;
    readonly is_read: boolean;
    readonly created_at: string;
}

export interface NotificationListResponse {
    readonly unread: number;
    readonly total: number;
    readonly items: readonly NotificationResponse[];
}

export interface NotificationStateResponse {
    readonly unread: number;
    readonly total: number;
    readonly changed: number;
}

export interface FaqEntryResponse {
    readonly id: number;
    readonly category: string;
    readonly question: string;
    readonly answer: string;
    readonly position: number;
    readonly is_published: boolean;
}

export interface FaqListResponse {
    readonly count: number;
    readonly entries: readonly FaqEntryResponse[];
}

export interface StatisticsResponse {
    readonly online_now: number;
    readonly online_signed_in: number;
    readonly visitors_window: number;
    readonly tickets_waiting: number;
}

export type StatisticsKey = keyof StatisticsResponse;

export type ToastLevel = "success" | "error" | "info";

/** Kind of a reaction a member may leave under a forum post. */
export type ReactionKind = "LIKE" | "LOVE" | "LAUGH" | "SURPRISED" | "SAD" | "HELPFUL";

export interface ReactionState {
    readonly total: number;
    readonly counts: Readonly<Record<string, number>>;
    readonly mine: readonly string[];
}

export interface PreviewResponse {
    readonly html: string;
}

export interface ApiError {
    readonly detail: string;
}

export const STATISTICS_KEYS: readonly StatisticsKey[] = [
    "online_now",
    "online_signed_in",
    "visitors_window",
    "tickets_waiting",
];

export const REACTION_KINDS: readonly ReactionKind[] = ["LIKE", "LOVE", "LAUGH", "SURPRISED", "SAD", "HELPFUL"];

/** Answer of the endpoint storing a picture pasted into the wiki editor. */
export interface WikiImageResponse {
    readonly id: number;
    readonly url: string;
    readonly markup: string;
}

/** One account inside a chat room. */
export interface ChatMemberResponse {
    readonly id: number;
    readonly username: string;
    readonly role: string;
}

/** One thing somebody said in a room. */
export interface ChatMessageResponse {
    readonly id: number;
    readonly chat_id: number;
    readonly author_id: number | null;
    readonly author: string;
    readonly body: string;
    readonly origin: string;
    readonly origin_label: string;
    readonly origin_icon: string;
    readonly created_at: string;
    readonly author_online: boolean;
}

/** One thing which happened to a room rather than in it. */
export interface ChatEventResponse {
    readonly id: number;
    readonly chat_id: number;
    readonly kind: string;
    readonly icon: string;
    readonly sentence: string;
    readonly created_at: string;
}

/** One room as the panel in the header lists it. */
export interface ChatSummaryResponse {
    readonly id: number;
    readonly kind: string;
    readonly kind_label: string;
    readonly name: string;
    readonly icon: string;
    readonly is_global: boolean;
    readonly can_manage: boolean;
    readonly unread: number;
    readonly preview: string;
    readonly preview_author: string;
    readonly preview_author_online: boolean;
    readonly last_message_at: string | null;
    readonly members: readonly ChatMemberResponse[];
}

/** Every room of the caller, plus where a stream should start reading. */
export interface ChatOverviewResponse {
    readonly me: number;
    readonly unread: number;
    readonly cursor: number;
    readonly items: readonly ChatSummaryResponse[];
    readonly muted_until: string | null;
    readonly muted_notice: string;
}

/** One page of a room, oldest first. */
export interface ChatMessagesResponse {
    readonly chat: ChatSummaryResponse;
    readonly items: readonly ChatMessageResponse[];
    readonly events: readonly ChatEventResponse[];
    readonly has_more: boolean;
}

/** The counters returned after a room was read or written to. */
export interface ChatStateResponse {
    readonly unread: number;
    readonly chat_unread: number;
}

/** One account the picker of a new room offers. */
export interface ChatCandidateResponse {
    readonly id: number;
    readonly username: string;
    readonly role: string;
}

/** The accounts the picker offers. */
export interface ChatCandidateListResponse {
    readonly items: readonly ChatCandidateResponse[];
}

/** Which rooms a browser has open, remembered across page loads. */
export interface ChatDockState {
    readonly open: readonly number[];
    readonly active: number | null;
    readonly collapsed: boolean;
}
